/*eslint no-restricted-exports: ["error", {
  "restrictedNamedExportsPattern": "designSystemColourPrimitives"
}]*/

/** PLEASE DO NOT EXPORT THIS, only use the semantic tokens below */
const designSystemColourPrimitives = {
  whites: {
    0: "#FFFFFF",
    100: "#FAFAFA",
    200: "#F6F6F6",
    300: "#EDEDED",
    400: "#DBDBDB",
    500: "#C4C4C4",
    600: "#949494"
  },
  blacks: {
    700: "#767676",
    800: "#575757",
    900: "#1D1D1D",
    1000: "#000000"
  },
  pinks: {
    100: "#FFF6F8",
    200: "#F6CED6",
    300: "#F2B8C3",
    400: "#FAA4B3",
    500: "#FF8FA2",
    600: "#FF7276",
    1000: "#DF2B49"
  },
  blues: {
    100: "#E5EEF3",
    200: "#CCF0FA",
    300: "#AFDEF0",
    400: "#95C7E1",
    500: "#71A9CE",
    600: "#5285B1",
    700: "#386594",
    800: "#244777",
    900: "#2D4452"
  },
  greens: {
    100: "#E0E8DF",
    200: "#DEF3D4",
    300: "#BCDDB2",
    400: "#94BB8D",
    500: "#638E60",
    600: "#467A47",
    700: "#306635",
    800: "#1E5228",
    900: "#283926"
  },
  yellows: {
    100: "#FFF2D9",
    500: "#FFBF3F",
    900: "#664C19"
  },
  reds: {
    100: "#E9D8D7",
    500: "#913A37",
    900: "#3A1716"
  },
  transparency: {
    light: "#FFFFFF50",
    medium: "#1D1D1B50",
    heavy: "#1D1D1B50",
    pink: "#F6CED660"
  },
  shadows: {
    level1: "rgba(29, 29, 29, 0.1000)",
    level2: "rgba(29, 29, 29, 0.1200)"
  }
} as const;

// Leaving this here in case we need to temporarily add colours that aren't in the design system
export const legacyColours = {} as const;

// Please leave this, this is specifically for if Studio requests specific colours for Dom to use that won't be incorporated into the design system, this should not be used for anything other than studio requests
export const studioColours = {} as const;

/**
 * These semantic colour tokens represent various design system colour groupings.
 * They should be used instead of the primitives to maintain consistency between Design and Engineering.
 *
 * @constant
 * @type {ColourToken}
 * @property {Object} primary - The primary colours.
 * @property {Object} primary.light - `#767676` (Light)
 * @property {Object} primary.medium - `#575757` (Medium)
 * @property {Object} primary.dark - `#1D1D1D` (Dark)
 *
 * @property {Object} secondary - The secondary colours.
 * @property {Object} secondary.light - `#FFFFFF` (Light)
 * @property {Object} secondary.soft - `#FAFAFA` (Soft)
 * @property {Object} secondary.warm - `#F6F6F6` (Warm)
 * @property {Object} secondary.medium - `#EDEDED` (Medium)
 * @property {Object} secondary.strong - `#DBDBDB` (Strong)
 * @property {Object} secondary.deep - `#C4C4C4` (Deep)
 * @property {Object} secondary.dark - `#949494` (Dark)
 *
 * @property {Object} accent - The accent colours.
 * @property {Object} accent.light - `#FFF6F8` (Light)
 * @property {Object} accent.soft - `#F6CED6` (Soft)
 * @property {Object} accent.warm - `#F2B8C3` (Warm)
 * @property {Object} accent.medium - `#FAA4B3` (Medium)
 * @property {Object} accent.strong - `#FF8FA2` (Strong)
 * @property {Object} accent.deep - `#FF7276` (Deep)
 * @property {Object} accent.dark - `#DF2B49` (Dark)
 *
 * @property {Object} info - The informational colours.
 * @property {string} info.light - `#E5EEF3` (Light)
 * @property {string} info.medium - `#71A9CE` (Outline)
 * @property {string} info.dark - `#244777` (Dark)
 *
 * @property {Object} success - The success colours.
 * @property {string} success.light - `#E0E8DF` (Light)
 * @property {string} success.medium - `#638E60` (Medium)
 * @property {string} success.dark - `#283926` (Dark)
 *
 * @property {Object} warning - The warning colours.
 * @property {string} warning.light - `#FFF2D9` (Light)
 * @property {string} warning.medium - `#FFBF3F` (Medium)
 * @property {string} warning.dark - `#664C19` (Dark)
 *
 * @property {Object} error - The error colours.
 * @property {string} error.light - `#E9D8D7` (Light)
 * @property {string} error.medium - `#913A37` (Medium)
 * @property {string} error.dark - `#3A1716` (Dark)
 *
 * @property {Object} transparency - The transparency colours.
 * @property {string} transparency.light - `#FFFFFF50` (Light)
 * @property {string} transparency.medium - `#1D1D1B50` (Medium)
 * @property {string} transparency.dark - `#1D1D1B50` (Heavy)
 * @property {string} transparency.accent - `#F6CED660` (Accent)
 */
export const colours = {
  primary: {
    light: designSystemColourPrimitives.blacks[700],
    medium: designSystemColourPrimitives.blacks[800],
    dark: designSystemColourPrimitives.blacks[900]
  },
  secondary: {
    light: designSystemColourPrimitives.whites[0],
    soft: designSystemColourPrimitives.whites[100],
    warm: designSystemColourPrimitives.whites[200],
    medium: designSystemColourPrimitives.whites[300],
    strong: designSystemColourPrimitives.whites[400],
    deep: designSystemColourPrimitives.whites[500],
    dark: designSystemColourPrimitives.whites[600]
  },
  accent: {
    light: designSystemColourPrimitives.pinks[100],
    soft: designSystemColourPrimitives.pinks[200],
    warm: designSystemColourPrimitives.pinks[300],
    medium: designSystemColourPrimitives.pinks[400],
    strong: designSystemColourPrimitives.pinks[500],
    deep: designSystemColourPrimitives.pinks[600],
    dark: designSystemColourPrimitives.pinks[1000]
  },
  info: {
    light: designSystemColourPrimitives.blues[100],
    medium: designSystemColourPrimitives.blues[500],
    dark: designSystemColourPrimitives.blues[900]
  },
  success: {
    light: designSystemColourPrimitives.greens[100],
    medium: designSystemColourPrimitives.greens[500],
    dark: designSystemColourPrimitives.greens[900]
  },
  warning: {
    light: designSystemColourPrimitives.yellows[100],
    medium: designSystemColourPrimitives.yellows[500],
    dark: designSystemColourPrimitives.yellows[900]
  },
  error: {
    light: designSystemColourPrimitives.reds[100],
    medium: designSystemColourPrimitives.reds[500],
    dark: designSystemColourPrimitives.reds[900]
  },
  transparency: {
    light: designSystemColourPrimitives.transparency.light,
    medium: designSystemColourPrimitives.transparency.medium,
    dark: designSystemColourPrimitives.transparency.heavy,
    accent: designSystemColourPrimitives.transparency.pink
  },
  shadows: {
    light: designSystemColourPrimitives.shadows.level1,
    medium: designSystemColourPrimitives.shadows.level2
  }
} as const;

export type ColourTokenCategory = keyof typeof colours;
export type ColourTokenShade<T extends ColourTokenCategory> =
  keyof (typeof colours)[T] & string;
export type ColourVariant = {
  [C in ColourTokenCategory]: C extends "transparency"
    ? never
    : `${C & string}-${ColourTokenShade<C>}`;
}[ColourTokenCategory];
